import { CLUSTER_LAYER_SUFFIX, MAP_ICON_NAMES } from "@/common/constants/maps";

export { _getDefaultMarkerOptions as getDefaultMarkerOptions };
export { _getDefaultPolygonOptions as getDefaultPolygonOptions };
export { _getDefaultGeohashMarkerOptions as getDefaultGeohashMarkerOptions };
export { _getDefaultHeatMapOptions as getDefaultHeatMapOptions };
export { _getMapDrawStyles as getMapDrawStyles };

const MAP_SWITCH_ZOOM_LEVEL = 11;

const _getDefaultMarkerOptions = (params) => {
  if (!params.mapSwitchZoomLevel || isNaN(params.mapSwitchZoomLevel)) {
    params.mapSwitchZoomLevel = MAP_SWITCH_ZOOM_LEVEL;
  }

  let iconName = MAP_ICON_NAMES.news;
  if (params.iconName) {
    iconName = params.iconName;
  }

  let options = {
    markerLayerOptions: {
      id: params.id + "-marker",
      type: "symbol",
      source: params.id,
      filter: ["!has", "point_count"],
      layout: {
        "icon-allow-overlap": true,
        "icon-image": iconName,
        "icon-size": 0.65,
      },
    },
  };

  if (!params.noCluster) {
    options["markerClusterLayerOptions"] = {
      id: params.id + CLUSTER_LAYER_SUFFIX,
      type: "symbol",
      source: params.id,
      filter: ["has", "point_count"],
      layout: {
        "icon-allow-overlap": true,
        "icon-image": iconName,
        "icon-size": 0.9,
      },
    };
  }
  return options;
};

const _getDefaultPolygonOptions = (params) => {
  let color = params.iconColor ? params.iconColor : "#2981ca";

  let options = {
    polygonLayerOptions: {
      id: params.id + "-polygon",
      type: "fill",
      source: params.id,
      paint: {
        "fill-color": color,
        "fill-opacity": params.opacity ? params.opacity : 0.5,
      },
    },
  };

  if (params.noFill) {
    options.polygonLayerOptions.type = "line";
    options.polygonLayerOptions.paint = {
      "line-width": 1,
      "line-color": color,
    };
  }

  return options;
};

const _getDefaultGeohashMarkerOptions = (params) => {
  let iconName = MAP_ICON_NAMES.news;
  if (params.iconName) {
    iconName = params.iconName;
  }
  let sourceId = params.id + "-geohash";

  if (isNaN(params.mapSwitchZoomLevel)) {
    params.mapSwitchZoomLevel = MAP_SWITCH_ZOOM_LEVEL;
  }

  let options = {
    markerLayerOptions: {
      id: params.id + "-geohash",
      type: "symbol",
      source: sourceId,
      filter: ["!has", "point_count"],
      layout: {
        "icon-allow-overlap": true,
        "icon-image": iconName,
        "icon-size": 0.65,
      },
    },
  };

  if (!params.noCluster) {
    options["markerClusterLayerOptions"] = {
      id: params.id + "-geohash-cluster",
      type: "symbol",
      source: sourceId,
      filter: ["has", "point_count"],
      layout: {
        "icon-allow-overlap": true,
        "icon-image": iconName,
        "icon-size": 0.9,
      },
    };
  }
  return options;
};

const _getDefaultHeatMapOptions = (params) => {
  let sourceId = params.id + "-geohash";

  let options = {
    heatmapLayerOptions: {
      id: params.id + "-geohash",
      type: "heatmap",
      source: sourceId,
      // "maxzoom": 15,
      paint: {
        // increase weight as diameter breast height increases
        "heatmap-weight": {
          //TODO this probably needs to be the document count but honestly it doesn't seem to matter
          property: "dbh",
          type: "exponential",
          stops: [
            [1, 0],
            [62, 1],
          ],
        },
        // increase intensity as zoom level increases
        "heatmap-intensity": {
          stops: [
            [11, 1],
            [22, 3],
          ],
        },
        // increase radius as zoom increases
        "heatmap-radius": {
          stops: [
            [11, 15],
            [22, 20],
          ],
        },
        // decrease opacity to transition into the circle layer
        "heatmap-opacity": {
          default: 0.8,
          stops: [
            [14, 0.8],
            [22, 0.8],
          ],
        },
        "heatmap-color": [
          "interpolate",
          ["linear"],
          ["heatmap-density"],
          0,
          "rgba(223,2,2,0)",
          0.2,
          "rgba(198,2,1,0.85)",
          0.4,
          "rgba(180,0,0,0.85)",
          0.6,
          "rgba(149,0,0,0.85)",
          0.8,
          "rgba(121,0,0,0.9)",
        ],
      },
    },
  };

  return options;
};

const _getMapDrawStyles = () => {
  return [
    {
      id: "gl-draw-point-active",
      type: "circle",
      filter: ["==", "active", "true"],
      paint: {
        "circle-stroke-width": 1,
        "circle-color": "#00913c",
        "circle-radius": 7,
      },
    },
    {
      id: "gl-draw-point-inactive",
      type: "circle",
      filter: ["==", "active", "false"],
      paint: {
        "circle-stroke-width": 1,
        "circle-color": "#0000ff",
        "circle-radius": 7,
      },
    },
    {
      id: "gl-draw-polygon-fill-inactive",
      type: "fill",
      filter: [
        "all",
        ["==", "active", "false"],
        ["==", "$type", "Polygon"],
        ["!=", "mode", "static"],
      ],
      paint: {
        "fill-color": "#0000ff",
        "fill-outline-color": "#0000ff",
        "fill-opacity": 0.3,
      },
    },
    {
      id: "gl-draw-polygon-fill-active",
      type: "fill",
      filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
      paint: {
        "fill-color": "#00913c",
        "fill-outline-color": "#00913c",
        "fill-opacity": 0.3,
      },
    },
    {
      id: "gl-draw-polygon-midpoint",
      type: "circle",
      filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
      paint: {
        "circle-radius": 7,
        "circle-color": "#00913c",
      },
    },
    {
      id: "gl-draw-polygon-stroke-inactive",
      type: "line",
      filter: [
        "all",
        ["==", "active", "false"],
        ["==", "$type", "Polygon"],
        ["!=", "mode", "static"],
      ],
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": "#0000ff",
        "line-width": 2,
      },
    },
    {
      id: "gl-draw-polygon-stroke-active",
      type: "line",
      filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": "#00913c",
        "line-width": 2,
      },
    },
    {
      id: "gl-draw-line-inactive",
      type: "line",
      filter: [
        "all",
        ["==", "active", "false"],
        ["==", "$type", "LineString"],
        ["!=", "mode", "static"],
      ],
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": "#0000ff",
        "line-width": 2,
      },
    },
    {
      id: "gl-draw-line-active",
      type: "line",
      filter: ["all", ["==", "$type", "LineString"], ["==", "active", "true"]],
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": "#00913c",
        "line-width": 2,
      },
    },
    {
      id: "gl-draw-polygon-and-line-vertex-stroke-inactive",
      type: "circle",
      filter: [
        "all",
        ["==", "meta", "vertex"],
        ["==", "$type", "Point"],
        ["!=", "mode", "static"],
      ],
      paint: {
        "circle-radius": 11,
        "circle-color": "#fff",
      },
    },
    {
      id: "gl-draw-polygon-and-line-vertex-inactive",
      type: "circle",
      filter: [
        "all",
        ["==", "meta", "vertex"],
        ["==", "$type", "Point"],
        ["!=", "mode", "static"],
      ],
      paint: {
        "circle-radius": 9,
        "circle-color": "#00913c",
      },
    },
  ];
};
